/**
 * CdnLiveStatus — the "are my published changes actually being served?" badge.
 *
 * `live` from the server is a GLOBAL guarantee (the deploy's invalidation
 * completed — verified against the deploy's own identity stamp — or a full
 * edge TTL elapsed) — never a single sampled edge, which a PO once trusted
 * while their SCORM tab kept serving stale content. For pointer courses the
 * verdict is COMPOSITE: green requires every referenced lesson package live
 * (and on its latest published revision) too, since lesson edits ship in
 * those packages while the course pointer bytes stay identical. While not
 * yet live, the badge says which path it's on; degraded provenance
 * (invalidation skipped/FAILED, partial uploads, stale lesson revisions)
 * renders loudly — a silent slow green is exactly the failure mode this
 * badge exists to prevent.
 *
 * With `expectedDeployId` set (publish-results usage), the badge verifies THE
 * deploy that produced it — if a newer deploy replaces it, the badge says
 * "superseded" instead of certifying work this publish didn't do.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { designTokens } from '@course-authoring/shared-types';
import { fetchPackageLiveStatus, type PackageLiveStatus } from '../api/publishApi';

const POLL_INTERVAL_MS = 10_000;
// ~7 minutes of auto-polling, then manual "Check now" only: must outlast the
// TTL-fallback window (5 min edge TTL + server margin) so a deploy without a
// working invalidation still flips green unattended.
const MAX_AUTO_POLLS = 42;

export type CdnStatus =
  | 'checking'
  | 'live'
  | 'propagating'
  | 'invalidating'
  | 'lessons-propagating'
  | 'lessons-unverified'
  | 'lesson-missing'
  | 'lesson-stale'
  | 'lesson-partial'
  | 'partial-deploy'
  | 'superseded'
  | 'unverified'
  | 'ttl-window'
  | 'deploy-missing'
  | 'not-deployed'
  | 'unavailable';

/**
 * The deployed package predating the last publish by more than this margin
 * means the publish's deploy step never completed (e.g. the account list
 * wasn't loaded) — edge==origin would then read "live" while serving the
 * PREVIOUS content. The margin absorbs S3-vs-DB clock skew; a genuinely
 * missed deploy lags by the full publish gap, which only grows.
 */
const DEPLOY_GAP_TOLERANCE_MS = 2 * 60_000;

/** Course/lesson wording: the publish happened, its deploy step did not. */
const DEFAULT_DEPLOY_GAP_COPY = {
  title: '⚠ Package delivery didn’t complete for this publish',
  detail: 'publish an update to retry',
} as const;

const styles: Record<string, CSSProperties> = {
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '6px',
    fontSize: '13px',
    flexWrap: 'wrap',
  },
  live: { color: designTokens.colors.workflowPublished, fontWeight: 600 },
  propagating: { color: designTokens.colors.workflowDraft, fontWeight: 600 },
  failure: { color: designTokens.colors.errorText, fontWeight: 600 },
  muted: { color: designTokens.colors.workflowArchived },
  label: { color: designTokens.colors.workflowArchived, fontWeight: 600 },
  checkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: designTokens.colors.selectionBorder,
    cursor: 'pointer',
    fontSize: '13px',
    textDecoration: 'underline',
  },
};

const formatTime = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export function CdnLiveStatus({
  packageId,
  accountSlug,
  pollToken,
  publishedAt,
  expectedDeployId,
  label,
  onStatusChange,
  supersededBehavior = 'terminal',
  fetchStatus = fetchPackageLiveStatus,
  deployGapCopy = DEFAULT_DEPLOY_GAP_COPY,
}: {
  packageId: string;
  /** Scope to check; omitted = the base (Emtrain) scope. */
  accountSlug?: string;
  /** Any change (e.g. lastPublishedAt) restarts the auto-poll cycle. */
  pollToken?: string | null;
  /** Last publish time; a deployed package much older than this flags a missed deploy. */
  publishedAt?: string | null;
  /**
   * Verify THIS deploy specifically (publish-results usage). When the hosted
   * deploy differs, the badge reports "superseded" instead of certifying a
   * deploy this publish didn't produce.
   */
  expectedDeployId?: string;
  /** Optional scope label rendered before the status (multi-scope lists). */
  label?: string;
  /**
   * Fires on every settled verdict (never 'checking') so hosts — the delivery
   * status page's rows — can reconcile their own presentation with the
   * served-truth result instead of contradicting it.
   */
  onStatusChange?: (status: CdnStatus, checkedAt: string | null) => void;
  /**
   * How a deploy-identity mismatch resolves. 'terminal' (default,
   * publish-results pinning): report "superseded" and stop — this publish's
   * verification is moot. 'evaluate' (delivery page): a different deploy owns
   * hosting now, so fall through and report the CURRENT hosted truth instead
   * of a terminal state that never reconciles the row.
   */
  supersededBehavior?: 'terminal' | 'evaluate';
  /**
   * Which delivery check to call. Defaults to the course/lesson endpoint;
   * questionnaire packages pass their own, since they are hosted under a
   * different path. The verdict semantics are identical either way — the server
   * shares one evaluator — so every state below applies unchanged.
   */
  fetchStatus?: (packageId: string, accountSlug?: string) => Promise<PackageLiveStatus>;
  /**
   * Copy for the "hosted revision predates the authored one" verdict. The default
   * describes a course's cause — a publish whose deploy step never ran. Hosts
   * whose content is published elsewhere (questionnaires: authored in
   * wh-frontend, packaged here) pass their own, because for them the same verdict
   * means "edited since the last publish". The remedy is identical either way, so
   * only the explanation differs.
   */
  deployGapCopy?: { title: string; detail: string };
}) {
  const [status, setStatus] = useState<CdnStatus>('checking');
  const [checkedAt, setCheckedAt] = useState<string>('');
  /**
   * In the TTL fallback, WHY the invalidation can't vouch for propagation:
   * 'failed' (creation threw), 'skipped' (no distribution configured), or
   * 'unverifiable' (created, but completion can't be confirmed — e.g. the
   * server's role lacks cloudfront:GetInvalidation). Distinct copy each: the
   * repair differs.
   */
  const [ttlCause, setTtlCause] = useState<'failed' | 'skipped' | 'unverifiable'>('unverifiable');
  /** Composite verdict: how many referenced lesson packages are not live yet. */
  const [lessonsPending, setLessonsPending] = useState(0);
  /** Composite verdict: lessons whose check itself failed (S3 hiccup) — polling continues. */
  const [lessonsUnavailable, setLessonsUnavailable] = useState(0);
  /** Composite verdict: some pending lesson rides the cache-expiry path (its invalidation failed). */
  const [lessonInvalidationFailed, setLessonInvalidationFailed] = useState(false);
  const pollsLeft = useRef(MAX_AUTO_POLLS);
  const timerRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);
  /**
   * Monotonic request generation: every check() invalidates all in-flight
   * predecessors, so a slow response from a previous scope/publish can never
   * overwrite the current one's state.
   */
  const requestSeqRef = useRef(0);

  const check = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    setStatus((current) => (current === 'live' ? current : 'checking'));
    try {
      const result = await fetchStatus(packageId, accountSlug);
      if (cancelledRef.current || seq !== requestSeqRef.current) return;
      setCheckedAt(result.checkedAt);
      if (!result.deployed) {
        // With an expectation pinned, a vanished package is a FAILED
        // verification; without one it is simply not delivered yet — either
        // way the row renders a verdict (hiding it is a pass-by-omission).
        setStatus(expectedDeployId ? 'deploy-missing' : 'not-deployed');
        return;
      }
      if (result.reason === 'stamp-malformed') {
        // Present-but-unparseable identity: unverifiable, not legacy — and
        // not self-healing, so no auto-poll.
        setStatus('unverified');
        return;
      }
      // The REVISION gap check runs BEFORE identity comparison: a hosted
      // package from a different deploy whose revision predates this publish
      // is a missed delivery ("superseded" would misread it as a benign newer
      // publish taking over).
      const publishTime = publishedAt ? new Date(publishedAt).getTime() : NaN;
      // Stamped revision beats the lastModified heuristic: the hosted
      // contentPublishedAt compares near-exact (1s epsilon — same DB clock),
      // so a publish missed by even seconds is caught; the 2-minute skew
      // margin remains only for pre-stamp legacy packages.
      const stampedTime = result.contentPublishedAt ? Date.parse(result.contentPublishedAt) : NaN;
      const originTime = result.origin?.lastModified ? new Date(result.origin.lastModified).getTime() : NaN;
      const gapDetected = Number.isFinite(publishTime) && (
        Number.isFinite(stampedTime)
          ? stampedTime + 1_000 < publishTime
          : Number.isFinite(originTime) && originTime + DEPLOY_GAP_TOLERANCE_MS < publishTime
      );
      if (gapDetected) {
        // Edge==origin would say "live", but the hosted revision predates the
        // publish: the deploy step never ran for it. Don't auto-poll — it
        // can't self-heal.
        setStatus('deploy-missing');
        return;
      }
      if (expectedDeployId) {
        // Exact-identity verification fails CLOSED: green requires the hosted
        // deploy to be exactly the one this publish produced. A response with
        // no identity at all (stamp/meta lost) cannot be verified — that is a
        // failure to prove, never an implicit pass.
        if (!result.deployId) {
          setStatus('unverified');
          return;
        }
        if (result.deployId !== expectedDeployId && supersededBehavior === 'terminal') {
          // A newer deploy owns delivery now (its revision is NOT behind this
          // publish — the gap check above ran first).
          setStatus('superseded');
          return;
        }
        // 'evaluate': the mismatching deploy owns hosting — fall through and
        // report ITS liveness so the page row reconciles to hosted truth.
      }
      if (result.live) {
        setStatus('live');
        setLessonsPending(0);
        return;
      }
      if (result.reason === 'partial-deploy') {
        // Uploads did not all land — the hosted package may be a mix of two
        // deploys. Can't self-heal; the repair is a publish update.
        setStatus('partial-deploy');
        return;
      }
      const lessonEntries = result.lessons ?? [];
      if (result.reason === 'lessons-unverified') {
        // Unverifiable lesson evidence cannot self-heal — no auto-poll.
        setLessonsUnavailable(lessonEntries.filter(
          (entry) => entry.reason === 'status-unavailable'
            || entry.reason === 'revision-unverified'
            || entry.reason === 'stamp-malformed',
        ).length);
        setStatus('lessons-unverified');
        return;
      }
      if (result.reason === 'lessons-propagating'
        && lessonEntries.some((entry) => entry.reason === 'not-deployed')) {
        // A hosted course pointer referencing a missing lesson package is a
        // broken course, not a propagation delay. Don't auto-poll — it can't
        // self-heal; the repair is a publish update.
        setStatus('lesson-missing');
        return;
      }
      if (result.reason === 'lessons-propagating'
        && lessonEntries.some((entry) => entry.reason === 'stale-revision')) {
        // The hosted lesson package propagated fine but is an OLDER revision
        // than the lesson's latest publish — its deploy never landed.
        setStatus('lesson-stale');
        return;
      }
      if (result.reason === 'lessons-propagating'
        && lessonEntries.some((entry) => entry.reason === 'partial-deploy')) {
        // A lesson package's uploads never completed — inconsistent hosted
        // content, not a delay. Can't self-heal; publish an update.
        setStatus('lesson-partial');
        return;
      }
      if (result.reason === 'lessons-propagating') {
        setLessonsPending(lessonEntries.filter((entry) => !entry.live).length);
        setLessonsUnavailable(lessonEntries.filter(
          (entry) => entry.reason === 'status-unavailable'
            || entry.reason === 'revision-unverified'
            || entry.reason === 'stamp-malformed',
        ).length);
        setLessonInvalidationFailed(lessonEntries.some(
          (entry) => !entry.live && entry.invalidation?.state === 'failed',
        ));
        setStatus('lessons-propagating');
      } else if (result.reason === 'invalidation-in-progress') {
        setStatus('invalidating');
      } else if (result.reason === 'ttl-window') {
        setStatus('ttl-window');
        setTtlCause(
          result.invalidation?.state === 'failed' ? 'failed'
            : result.invalidation?.state === 'skipped' ? 'skipped'
            : 'unverifiable',
        );
      } else {
        setStatus('propagating');
      }
      if (pollsLeft.current > 0) {
        pollsLeft.current -= 1;
        timerRef.current = window.setTimeout(() => { void check(); }, POLL_INTERVAL_MS);
      }
    } catch {
      if (!cancelledRef.current && seq === requestSeqRef.current) setStatus('unavailable');
    }
  }, [packageId, accountSlug, publishedAt, expectedDeployId, supersededBehavior, fetchStatus]);

  useEffect(() => {
    cancelledRef.current = false;
    pollsLeft.current = MAX_AUTO_POLLS;
    // A new scope/publish must never inherit the previous one's verdict —
    // reset hard; check() bumps the generation, discarding stale in-flights.
    setStatus('checking');
    setLessonsPending(0);
    setLessonsUnavailable(0);
    setLessonInvalidationFailed(false);
    setTtlCause('unverifiable');
    void check();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [check, pollToken]);

  const manualCheck = () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    pollsLeft.current = MAX_AUTO_POLLS;
    void check();
  };

  // Latest-callback ref: hosts pass inline closures; the verdict effect must
  // not re-fire on every render, only on actual status changes.
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  useEffect(() => {
    if (status === 'checking') return;
    onStatusChangeRef.current?.(status, checkedAt || null);
  }, [status, checkedAt]);

  return (
    <div style={styles.row} role="status" aria-live="polite">
      {label && <span style={styles.label}>{label}</span>}
      {status === 'live' && (
        <>
          <span style={styles.live}>✓ Live on CDN</span>
          {checkedAt && <span style={styles.muted}>checked {formatTime(checkedAt)}</span>}
          <span style={styles.muted}>· reload any open player tab to pick it up</span>
        </>
      )}
      {status === 'propagating' && (
        <>
          <span style={styles.propagating}>CDN propagating…</span>
          <span style={styles.muted}>rechecking automatically; published changes are not served yet</span>
        </>
      )}
      {status === 'invalidating' && (
        <>
          <span style={styles.propagating}>CDN invalidation in progress…</span>
          <span style={styles.muted}>goes green once every edge region serves this publish</span>
        </>
      )}
      {status === 'lessons-propagating' && (
        <>
          <span style={styles.propagating}>Course package live — lesson content still propagating…</span>
          <span style={styles.muted}>
            {lessonsPending === 1 ? '1 lesson package' : `${lessonsPending} lesson packages`} not
            fully served yet; rechecking automatically
            {lessonsUnavailable > 0 && ` · ${lessonsUnavailable} could not be checked`}
            {lessonInvalidationFailed && ' · a lesson’s invalidation failed — it refreshes by cache expiry (~6 min)'}
          </span>
        </>
      )}
      {status === 'lessons-unverified' && (
        <>
          <span style={styles.failure}>⚠ Lesson delivery can’t be verified for this scope</span>
          <span style={styles.muted}>
            {lessonsUnavailable === 1 ? '1 lesson package' : `${lessonsUnavailable} lesson packages`} could
            not be verified — recheck, or publish an update if this persists
          </span>
        </>
      )}
      {status === 'lesson-missing' && (
        <>
          <span style={styles.failure}>⚠ A referenced lesson package is missing for this account scope</span>
          <span style={styles.muted}>learners would hit a broken lesson — publish an update to repair it</span>
        </>
      )}
      {status === 'lesson-stale' && (
        <>
          <span style={styles.failure}>⚠ A lesson’s latest publish hasn’t reached delivery</span>
          <span style={styles.muted}>
            the hosted lesson package is an older revision — publish an update to redeliver it
          </span>
        </>
      )}
      {status === 'lesson-partial' && (
        <>
          <span style={styles.failure}>⚠ A lesson package didn’t fully upload for this scope</span>
          <span style={styles.muted}>hosted lesson content may be inconsistent — publish an update to repair it</span>
        </>
      )}
      {status === 'partial-deploy' && (
        <>
          <span style={styles.failure}>⚠ The last delivery for this scope didn’t fully upload</span>
          <span style={styles.muted}>hosted content may be inconsistent — publish an update to repair it</span>
        </>
      )}
      {status === 'superseded' && (
        <>
          <span style={styles.muted}>Superseded by a newer publish</span>
          <span style={styles.muted}>current delivery is verified against the latest publish instead</span>
        </>
      )}
      {status === 'unverified' && (
        <>
          <span style={styles.failure}>⚠ Delivery can’t be verified for this publish</span>
          <span style={styles.muted}>
            the hosted package carries no delivery identity — see any delivery caveats above, or publish an update to retry
          </span>
        </>
      )}
      {status === 'ttl-window' && ttlCause === 'failed' && (
        <>
          <span style={styles.failure}>⚠ CDN invalidation FAILED for this publish</span>
          <span style={styles.muted}>
            content refreshes by cache expiry instead — served everywhere within ~6 minutes of
            publish; if this repeats, check the server’s CloudFront permissions
          </span>
        </>
      )}
      {status === 'ttl-window' && ttlCause === 'skipped' && (
        <>
          <span style={styles.propagating}>⚠ CDN refreshing by cache expiry</span>
          <span style={styles.muted}>
            cache invalidation isn’t configured on this server — served everywhere within ~6 minutes of publish
          </span>
        </>
      )}
      {status === 'ttl-window' && ttlCause === 'unverifiable' && (
        <>
          <span style={styles.propagating}>⚠ CDN invalidation can’t be verified</span>
          <span style={styles.muted}>
            the purge was requested but its completion can’t be confirmed (the server may lack the
            cloudfront:GetInvalidation permission) — served everywhere within ~6 minutes of publish regardless
          </span>
        </>
      )}
      {status === 'deploy-missing' && (
        <>
          <span style={styles.failure}>{deployGapCopy.title}</span>
          <span style={styles.muted}>{deployGapCopy.detail}</span>
        </>
      )}
      {status === 'not-deployed' && (
        <>
          <span style={styles.muted}>○ Not delivered yet</span>
          <span style={styles.muted}>
            no package is hosted for this account context
            {checkedAt ? ` — checked ${formatTime(checkedAt)}` : ''}
          </span>
        </>
      )}
      {status === 'checking' && <span style={styles.muted}>Checking CDN…</span>}
      {status === 'unavailable' && <span style={styles.muted}>CDN check unavailable</span>}
      <button type="button" style={styles.checkButton} onClick={manualCheck}>
        Check now
      </button>
    </div>
  );
}
